import React from 'react';
import { Link } from 'react-router-dom';

// Home Features Component - displaying the key features of our sustainable computing initiative
function HomeFeatures() {
    const features = [
        {
            icon: "fa-recycle",
            title: "E-Waste Reduction",
            description: "Transform discarded hardware into valuable educational resources while reducing environmental impact.",
            metrics: [
                "2+ tons of e-waste diverted",
                "85% recovery rate target",
                "Zero landfill impact goal"
            ]
        },
        {
            icon: "fa-solar-panel",
            title: "100% Renewable Energy",
            description: "Powered entirely by sustainable energy sources for carbon-neutral computing.",
            metrics: [
                "250kW planned solar",
                "100kW planned wind",
                "500kWh proposed storage"
            ]
        },
        {
            icon: "fa-graduation-cap",
            title: "Educational Impact",
            description: "Bridging the digital divide by providing technology access to underserved communities.",
            metrics: [
                "15 schools supported",
                "5,000+ students reached",
                "3 countries served"
            ]
        },
        {
            icon: "fa-handshake",
            title: "Community Partnerships",
            description: "Building a global network of organizations committed to sustainable technology.",
            metrics: [
                "10+ active partnerships",
                "5+ community programs",
                "3 research collaborations"
            ]
        }
    ];

    return (
        <div>
            <section data-name="home-features" className="home-features py-24 bg-gray-50 relative overflow-hidden">
                <div className="absolute top-0 right-0 -z-10 w-1/2 h-1/2 opacity-10 animate-float-slow">
                    <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
                        <path fill="#10B981" d="M39.5,-65.3C53.5,-55.3,68.9,-47.6,76.8,-34.9C84.7,-22.3,85,-4.7,81.2,11.5C77.5,27.7,69.8,42.5,58.1,53.2C46.4,63.9,30.8,70.5,14.9,74.3C-1,78.1,-17.2,79,-31.1,73.2C-45,67.4,-56.5,54.9,-64.7,40.8C-72.9,26.7,-77.8,11,-77.8,-4.8C-77.8,-20.6,-72.8,-36.5,-62.4,-47.8C-51.9,-59.1,-35.9,-65.9,-20.9,-70.9C-5.9,-75.9,8.1,-79.1,20.8,-76.2C33.6,-73.3,45.1,-64.5,39.5,-65.3Z" transform="translate(100 100)" />
                    </svg>
                </div>
                
                <div className="container mx-auto px-4 relative z-10">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold mb-6 text-gray-900">
                                Sustainable <span className="text-gradient bg-gradient-to-r from-green-600 to-emerald-700">Computing</span> For All
                            </h2>
                            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                                Our innovative approach combines environmental sustainability with educational equity, creating lasting impact for people and planet.
                            </p>
                            <div className="flex justify-center space-x-4 mb-16">
                                <div className="bg-white rounded-lg shadow-lg p-4 flex items-center space-x-4">
                                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                                        <i className="fas fa-leaf text-green-600 text-2xl"></i>
                                    </div>
                                    <div className="text-left">
                                        <div className="text-2xl font-bold text-green-600">2+</div>
                                        <div className="text-gray-600">Tons E-Waste Saved</div>
                                    </div>
                                </div>
                                <div className="bg-white rounded-lg shadow-lg p-4 flex items-center space-x-4">
                                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                        <i className="fas fa-school text-blue-600 text-2xl"></i>
                                    </div>
                                    <div className="text-left">
                                        <div className="text-2xl font-bold text-blue-600">5,000+</div>
                                        <div className="text-gray-600">Students Reached</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {features.map((feature, index) => (
                                <FeatureCard key={index} feature={feature} index={index} />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Break Section with Image */}
            <section className="relative h-96 flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0">
                    <img 
                        src="https://images.unsplash.com/photo-1554769945-af468c934022?crop=entropy&cs=srgb&fm=jpg&ixid=M3wzMjA0NjB8MHwxfHNlYXJjaHwyfHxncmVlbiUyMHRlY2h8ZW58MHx8fHwxNzQyOTcwNjU5fDA&ixlib=rb-4.0.3&q=85"
                        alt="Wind turbines"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50"></div>
                </div>
                <div className="relative z-10 text-center px-4">
                    <h2 className="text-4xl md:text-5xl font-bold mb-4">
                        <span className="text-white">Powering </span>
                        <span className="text-green-400">Sustainable</span>
                        <span className="text-white"> Education</span>
                    </h2>
                    <p className="text-xl text-gray-200 max-w-2xl mx-auto">
                        Through renewable energy and recycled hardware, we're building a greener future for education
                    </p>
                </div>
            </section>

            {/* Environmental Impact Section */}
            <section className="py-24 bg-gray-50">
                <div className="container mx-auto px-4">
                    <h2 className="text-4xl font-bold text-center mb-12">
                        <span className="text-gray-800">Environmental </span>
                        <span className="text-green-600">Impact</span>
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
                        <div className="bg-white rounded-xl shadow-lg p-6 text-center">
                            <div className="text-4xl font-bold text-green-600 mb-2">2+</div>
                            <div className="text-lg text-gray-800">Tons E-Waste</div>
                            <div className="text-sm text-gray-600 mt-2">Diverted from landfills</div>
                        </div>
                        <div className="bg-white rounded-xl shadow-lg p-6 text-center">
                            <div className="text-4xl font-bold text-green-600 mb-2">85%</div>
                            <div className="text-lg text-gray-800">Recovery Target</div>
                            <div className="text-sm text-gray-600 mt-2">For repairable devices</div>
                        </div>
                        <div className="bg-white rounded-xl shadow-lg p-6 text-center">
                            <div className="text-4xl font-bold text-green-600 mb-2">100%</div>
                            <div className="text-lg text-gray-800">Clean Energy</div>
                            <div className="text-sm text-gray-600 mt-2">Planned renewable power</div>
                        </div>
                    </div>
                    
                    <div className="text-center">
                        <Link 
                            to="/impact"
                            className="btn btn-primary transform transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-green-500/20 group inline-block"
                        >
                            Learn More About Our Impact
                            <i className="fas fa-arrow-right ml-2 transition-transform duration-300 group-hover:translate-x-1"></i>
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}

// Create a simple FeatureCard component
function FeatureCard({ feature, index }) {
    return (
        <div 
            className="bg-white rounded-xl shadow-xl p-8 border border-gray-100 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 group relative overflow-hidden"
        >
            <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-blue-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            <div className="relative z-10">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6 group-hover:bg-green-200 transition-colors duration-300 group-hover:scale-110">
                    <i className={`fas ${feature.icon} text-green-600 text-2xl group-hover:scale-110 transition-transform duration-300`}></i>
                </div>
                
                <h3 className="text-xl font-bold mb-4 text-gray-800 group-hover:text-green-700 transition-colors duration-300">{feature.title}</h3>
                <p className="text-gray-600 group-hover:text-gray-700 transition-colors duration-300 mb-4">{feature.description}</p>
                
                <div className="space-y-3">
                    {feature.metrics && feature.metrics.map((metric, i) => (
                        <div key={i} className="flex items-center text-gray-600">
                            <i className="fas fa-check-circle text-green-500 mr-2"></i>
                            <span>{metric}</span>
                        </div>
                    ))}
                </div>
            </div>
            
            <div className="absolute -bottom-10 -right-10 w-20 h-20 bg-green-100 rounded-full opacity-0 group-hover:opacity-20 transition-opacity duration-500"></div>
            <div className="absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r from-green-400 to-green-600 group-hover:w-full transition-all duration-700 ease-in-out"></div>
        </div>
    );
}

export default HomeFeatures;
